import { FormEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clearIndusvivaAuth, indusvivaApi } from '../../lib/indusvivaApi';
import { useStore } from '../../store/StoreProvider';

function isValidCustomerPhone(phone: string) {
  return /^[6-9]\d{9}$/.test(phone) && !/^(\d)\1{9}$/.test(phone);
}

/**
 * Cart unlock via Indusviva customer OTP.
 * Partner/distributor numbers are rejected by /enrollment/phone-verify (same as official store).
 */
export function VerifyGate() {
  const navigate = useNavigate();
  const {
    showVerifyModal,
    setShowVerifyModal,
    setCustomer,
    customer,
    verifyReturnTo,
    verifySwitchAccount,
    setVerifySwitchAccount,
  } = useStore();
  const [step, setStep] = useState<'phone' | 'otp' | 'profile'>('phone');
  const [phone, setPhone] = useState(customer?.customer_phone || '');
  const [otp, setOtp] = useState('');
  const [name, setName] = useState(customer?.customer_name || '');
  const [email, setEmail] = useState(customer?.customer_email || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customerUserId, setCustomerUserId] = useState<string | number | null>(null);
  const wasOpen = useRef(false);

  // Reset form only when the modal opens (not mid-flow after Send OTP clears the old session).
  useEffect(() => {
    if (showVerifyModal && !wasOpen.current) {
      setStep('phone');
      setOtp('');
      setError(null);
      setCustomerUserId(null);
      if (verifySwitchAccount) {
        setPhone('');
        setName('');
        setEmail('');
      } else {
        setPhone(customer?.customer_phone || '');
        setName(customer?.customer_name || '');
        setEmail(customer?.customer_email || '');
      }
    }
    wasOpen.current = showVerifyModal;
  }, [showVerifyModal, verifySwitchAccount, customer]);

  if (!showVerifyModal) return null;

  const closeModal = () => {
    setVerifySwitchAccount(false);
    setShowVerifyModal(false);
  };

  const unlock = (session: {
    customer_id?: number | string;
    customer_user_id?: number | string;
    customer_name?: string;
    customer_phone?: string;
    customer_email?: string;
  }) => {
    setVerifySwitchAccount(false);
    setCustomer({ ...session, verified: true });
    setShowVerifyModal(false);
    navigate(verifyReturnTo || '/cart');
  };

  const loadCustomerOrSignup = async (userId: string | number | null) => {
    if (userId != null && userId !== '') {
      try {
        const details = await indusvivaApi.customerDetails({ customer_user_id: userId });
        const d = details as Record<string, unknown>;
        unlock({
          customer_id: (d.customer_id as string | number) || undefined,
          customer_user_id: userId,
          customer_name: String(d.customer_name || d.name || name || ''),
          customer_phone: phone,
          customer_email: String(d.customer_email || d.email || email || ''),
        });
        return;
      } catch {
        /* fall through to profile */
      }
    }
    setCustomerUserId(userId);
    setStep('profile');
  };

  const sendOtp = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!isValidCustomerPhone(phone)) {
      setError('Enter a valid 10-digit customer mobile (not a partner ID phone)');
      return;
    }
    setLoading(true);
    try {
      // Official store always checks partner/distributor phone first
      await indusvivaApi.phoneCheck({ phone });
      await indusvivaApi.sendOtp({ phone });
      // Commit silent logout only after OTP is successfully sent
      clearIndusvivaAuth();
      setCustomer(null);
      setVerifySwitchAccount(false);
      setStep('otp');
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Unable to send OTP. Partner numbers cannot be used as customer phones.',
      );
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!otp.trim()) {
      setError('Enter the OTP sent to your phone');
      return;
    }
    setLoading(true);
    try {
      // Indusviva expects phone_otp (not otp)
      const verified = await indusvivaApi.verifyOtp({ phone, phone_otp: otp.trim() });
      const userId =
        typeof verified === 'string' || typeof verified === 'number'
          ? verified
          : verified && typeof verified === 'object'
            ? ((verified as Record<string, unknown>).customer_user_id as string | number) || null
            : null;
      await loadCustomerOrSignup(userId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'OTP verification failed');
    } finally {
      setLoading(false);
    }
  };

  const finishSignup = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Enter your full name');
      return;
    }
    if (!email.trim()) {
      setError('Enter your email (required by Indusviva)');
      return;
    }
    setLoading(true);
    try {
      const created = await indusvivaApi.signup({
        name: name.trim(),
        phone,
        email: email.trim(),
      });
      const d = created as Record<string, unknown>;
      unlock({
        customer_id: (d.customer_id as string | number) || undefined,
        customer_user_id: (d.customer_user_id as string | number) || customerUserId || undefined,
        customer_name: name.trim(),
        customer_phone: phone,
        customer_email: email.trim(),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ileafs-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="verify-title">
      <div className="ileafs-modal">
        <button type="button" className="ileafs-modal__close" onClick={closeModal} aria-label="Close">
          ×
        </button>
        <h2 id="verify-title">
          {verifySwitchAccount
            ? 'Switch account'
            : verifyReturnTo === '/account'
              ? 'Verify to view account'
              : 'Verify to view cart'}
        </h2>
        <p>
          Use a <strong>customer</strong> mobile number. Verified partner / distributor phones are blocked by
          Indusviva (same as the official store).
        </p>

        {step === 'phone' ? (
          <form onSubmit={sendOtp} className="ileafs-modal__form">
            <label htmlFor="phone-input">Customer mobile number</label>
            <input
              id="phone-input"
              inputMode="numeric"
              maxLength={10}
              placeholder="10-digit customer mobile"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
              autoFocus
            />
            <button type="submit" className="vs-btn style2" disabled={loading}>
              {loading ? 'Checking…' : 'Send OTP'}
            </button>
          </form>
        ) : null}

        {step === 'otp' ? (
          <form onSubmit={verifyOtp} className="ileafs-modal__form">
            <label htmlFor="otp-input">Enter OTP</label>
            <input
              id="otp-input"
              inputMode="numeric"
              placeholder="OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value.trim())}
              autoFocus
            />
            <button type="submit" className="vs-btn style2" disabled={loading}>
              {loading ? 'Verifying…' : 'Verify OTP'}
            </button>
            <button type="button" className="ileafs-modal__link" onClick={() => setStep('phone')}>
              Change number
            </button>
          </form>
        ) : null}

        {step === 'profile' ? (
          <form onSubmit={finishSignup} className="ileafs-modal__form">
            <label htmlFor="name-input">Full name</label>
            <input
              id="name-input"
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              required
            />
            <label htmlFor="email-input">Email</label>
            <input
              id="email-input"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <button type="submit" className="vs-btn style2" disabled={loading}>
              {loading ? 'Saving…' : 'Continue to cart'}
            </button>
          </form>
        ) : null}

        {error ? <p className="ileafs-modal__error">{error}</p> : null}
      </div>
    </div>
  );
}
